/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { CapacitorSQLite, SQLiteConnection } from '@capacitor-community/sqlite'
import { BaseAdapter } from './base.adapter.js'
import { SQLiteMigrationManager } from '../migrations/sqlite.migrations.js'

/**
 * SQLite Adapter for Native Platforms (iOS/Android)
 * Wraps Capacitor SQLite plugin to provide unified interface
 * 
 * MIGRATION INFO:
 * - Baseline Version: 1 (established Dec 2025, native testing start)
 * - All schema changes before v1 are consolidated into baseline
 * - Uses PRAGMA user_version for version tracking
 * - Future migrations: see docs/NATIVE_MIGRATIONS_SQLITE.md
 */
export class SQLiteAdapter extends BaseAdapter {
  constructor() {
    super()
    this.sqlite = new SQLiteConnection(CapacitorSQLite)
    this.db = null
    this.DB_NAME = 'digiziege.db'
    this.migrationManager = null // Initialized after DB connection
  }

  /**
   * Initialize SQLite database with migration support
   * @returns {Promise<void>}
   */
  async initialize() {
    try {
      // Check if connection is consistent
      const ret = await this.sqlite.checkConnectionsConsistency()
      const isConn = (await this.sqlite.isConnection(this.DB_NAME, false))
        .result

      if (ret.result && isConn) {
        // Connection exists, retrieve it
        this.db = await this.sqlite.retrieveConnection(this.DB_NAME, false)
      } else {
        // Create new connection
        this.db = await this.sqlite.createConnection(
          this.DB_NAME,
          false,
          'no-encryption',
          this.DB_VERSION,
          false
        )
      }

      // Open the database
      await this.db.open()

      // Initialize migration manager
      this.migrationManager = new SQLiteMigrationManager(this.db)
      
      // Check current database version
      const currentVersion = await this.migrationManager.getCurrentVersion()
      console.log(`SQLite database version: ${currentVersion}`)

      if (currentVersion === 0) {
        // Fresh install: Create baseline schema
        console.log('Fresh install detected, creating baseline schema...')
        await this.createTables()
        await this.migrationManager.setVersion(this.migrationManager.getExpectedVersion())
        console.log(`SQLite initialized at baseline v${this.migrationManager.getExpectedVersion()}`)
      } else {
        // Run migrations if needed (handles version comparison internally)
        await this.migrationManager.initialize(currentVersion)
      }

      // Validate final version
      const finalVersion = await this.migrationManager.getCurrentVersion()
      const expectedVersion = this.migrationManager.getExpectedVersion()
      if (finalVersion !== expectedVersion) {
        throw new Error(
          `Database version mismatch: expected ${expectedVersion}, got ${finalVersion}`
        )
      }

      console.log('SQLite initialized successfully')
    } catch (error) {
      console.error('SQLite initialization failed:', error)
      throw error
    }
  }

  /**
   * Create database tables
   * @private
   * @returns {Promise<void>}
   */
  async createTables() {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    const schema = `
      -- Groups table (parent)
      CREATE TABLE IF NOT EXISTS groups (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        name TEXT UNIQUE NOT NULL,
        location TEXT,
        description TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP
      );

      CREATE INDEX IF NOT EXISTS idx_groups_name ON groups(name);

      -- Goats table (child - references groups)
      CREATE TABLE IF NOT EXISTS goats (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        name TEXT,
        date_of_birth TEXT NOT NULL,
        place_of_birth TEXT,
        breed TEXT NOT NULL,
        -- Required ear tag (official identification number) - unique identifier
        eartag TEXT NOT NULL UNIQUE,
        gender TEXT NOT NULL CHECK(gender IN ('Male', 'Female')),
        notes TEXT,
        group_id INTEGER,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
        FOREIGN KEY (group_id) REFERENCES groups(id) ON DELETE SET NULL
      );

      -- Weights table (child - references goats)
      CREATE TABLE IF NOT EXISTS weights (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        goat_id INTEGER NOT NULL,
        value REAL NOT NULL,
        measured_at TEXT DEFAULT CURRENT_TIMESTAMP,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
        FOREIGN KEY (goat_id) REFERENCES goats(id) ON DELETE CASCADE
      );

      CREATE INDEX IF NOT EXISTS idx_weights_goat ON weights(goat_id);
      CREATE INDEX IF NOT EXISTS idx_weights_measured ON weights(measured_at);

      -- Temperatures table (child - references goats)
      CREATE TABLE IF NOT EXISTS temperatures (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        goat_id INTEGER NOT NULL,
        value REAL NOT NULL,
        measured_at TEXT DEFAULT CURRENT_TIMESTAMP,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
        FOREIGN KEY (goat_id) REFERENCES goats(id) ON DELETE CASCADE
      );

      CREATE INDEX IF NOT EXISTS idx_temperatures_goat ON temperatures(goat_id);
      CREATE INDEX IF NOT EXISTS idx_temperatures_measured ON temperatures(measured_at);

      -- FAMACHA table (child - references goats)
      CREATE TABLE IF NOT EXISTS famacha (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        goat_id INTEGER NOT NULL,
        score INTEGER NOT NULL CHECK(score BETWEEN 1 AND 5),
        measured_at TEXT DEFAULT CURRENT_TIMESTAMP,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
        FOREIGN KEY (goat_id) REFERENCES goats(id) ON DELETE CASCADE
      );

      CREATE INDEX IF NOT EXISTS idx_famacha_goat ON famacha(goat_id);
      CREATE INDEX IF NOT EXISTS idx_famacha_measured ON famacha(measured_at);

            -- DAG table (child - references goats)
      CREATE TABLE IF NOT EXISTS dag (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        goat_id INTEGER NOT NULL,
        score INTEGER NOT NULL CHECK(score BETWEEN 1 AND 5),
        measured_at TEXT DEFAULT CURRENT_TIMESTAMP,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
        FOREIGN KEY (goat_id) REFERENCES goats(id) ON DELETE CASCADE
      );

      CREATE INDEX IF NOT EXISTS idx_dag_goat ON dag(goat_id);
      CREATE INDEX IF NOT EXISTS idx_dag_measured ON dag(measured_at);

      -- Health Records table (child - references goats)
      CREATE TABLE IF NOT EXISTS health_records (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        goat_id INTEGER NOT NULL,
        type TEXT NOT NULL CHECK(type IN ('wound', 'disease', 'injury', 'treatment', 'vaccination')),
        title TEXT NOT NULL,
        description TEXT NOT NULL,
        notes TEXT,
        measured_at TEXT DEFAULT CURRENT_TIMESTAMP,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
        FOREIGN KEY (goat_id) REFERENCES goats(id) ON DELETE CASCADE
      );

      CREATE INDEX IF NOT EXISTS idx_health_records_goat ON health_records(goat_id);
      CREATE INDEX IF NOT EXISTS idx_health_records_type ON health_records(type);
      CREATE INDEX IF NOT EXISTS idx_health_records_measured ON health_records(measured_at);


      CREATE INDEX IF NOT EXISTS idx_goats_name ON goats(name);
      CREATE INDEX IF NOT EXISTS idx_goats_group_id ON goats(group_id);
      CREATE INDEX IF NOT EXISTS idx_goats_breed ON goats(breed);
    `

    await this.db.execute(schema)
  }

  /**
   * Execute a SELECT query
   * Directly executes native SQLite queries (no translation needed)
   * @param {string} sql - SQL query string (e.g., 'SELECT * FROM goats WHERE id = ?')
   * @param {Array} params - Query parameters (?, ?, ...)
   * @returns {Promise<{values: Array}>} Query results with rows
   */
  async query(sql, params = []) {
    if (!this.db) {
      throw new Error('Database not initialized')
    }
    return await this.db.query(sql, params)
  }

  /**
   * Execute an INSERT, UPDATE, or DELETE command
   * @param {string} sql - SQL command string
   * @param {Array} params - Command parameters
   * @returns {Promise<{changes: {lastId: number}}>} Execution result
   */
  async run(sql, params = []) {
    if (!this.db) {
      throw new Error('Database not initialized')
    }
    return await this.db.run(sql, params)
  }

  /**
   * Execute raw SQL (for schema creation)
   * @param {string} sql - SQL statements
   * @returns {Promise<void>}
   */
  async execute(sql) {
    if (!this.db) {
      throw new Error('Database not initialized')
    }
    return await this.db.execute(sql)
  }

  /**
   * Close database connection
   * @returns {Promise<void>}
   */
  async close() {
    if (this.db) {
      await this.sqlite.closeConnection(this.DB_NAME, false)
      this.db = null
      console.log('SQLite connection closed')
    }
  }

  /**
   * Drop all tables and reset database schema
   * This is useful for development/testing to completely reset the database
   * @returns {Promise<void>}
   */
  async dropAllTables() {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    try {
      console.log('Dropping all tables from SQLite database...')

      // Drop tables in reverse order (child tables first to avoid FK constraints)
      const dropStatements = `
        DROP TABLE IF EXISTS health_records;
        DROP TABLE IF EXISTS dag;
        DROP TABLE IF EXISTS famacha;
        DROP TABLE IF EXISTS temperatures;
        DROP TABLE IF EXISTS weights;
        DROP TABLE IF EXISTS goats;
        DROP TABLE IF EXISTS groups;
      `

      await this.db.execute(dropStatements)

      console.log('All SQLite tables dropped successfully')
      console.log('Database will be recreated on next initialization')
    } catch (error) {
      console.error('Failed to drop SQLite tables:', error)
      throw error
    }
  }

  /**
   * Get adapter name
   * @returns {string}
   */
  getName() {
    return 'SQLite (Native)'
  }
}

// Export singleton instance
export const sqliteAdapter = new SQLiteAdapter()
